import { useState } from "react";
import { Badge, Container, Group, Stack, Tabs, Text, ThemeIcon, Title } from "@mantine/core";
import { IconLanguage, IconHistory, IconForms, IconPackages } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useTextTranslation } from "@hooks/translation/useTextTranslation";
import { useJobHistory } from "@hooks/translation/useJobHistory";
import { BatchJobsProvider, useSharedBatchJobs } from "@hooks/batch";
import { TextTranslationView } from "@features/TextTranslation";
import { BatchTranslationView } from "@features/BatchTranslation";
import { HistoryPanel } from "@features/JobHistory";
import type { HistoryEntry } from "@models/translation";

const TranslatePageInner: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>("text");
  const history = useJobHistory();
  const tx = useTextTranslation({ onSubmitted: history.add });
  const batchJobs = useSharedBatchJobs();

  const openFromHistory = (entry: HistoryEntry) => {
    tx.openJob(entry);
    setTab("text");
  };

  const activeJobsCount = batchJobs.jobs.filter(
    (j) => j.status !== "completed" && j.status !== "failed",
  ).length;

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group gap="sm" align="center">
          <ThemeIcon
            size={42}
            radius="md"
            variant="gradient"
            gradient={{ from: "brand.7", to: "brand.5", deg: 150 }}
            style={{
              boxShadow:
                "0 4px 14px color-mix(in srgb, var(--mantine-color-brand-7) 28%, transparent)",
            }}
          >
            <IconLanguage size={22} color="white" />
          </ThemeIcon>
          <div>
            <Title order={3} ff="heading">
              {t("translation.title")}
            </Title>
            <Text c="dimmed" size="xs">
              {t("translation.subtitle")}
            </Text>
          </div>
        </Group>

        <Tabs value={tab} onChange={(v) => setTab(v ?? "text")} color="brand" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="text" leftSection={<IconForms size={16} />}>
              {t("translation.tabs.text")}
            </Tabs.Tab>
            <Tabs.Tab
              value="batch"
              leftSection={<IconPackages size={16} />}
              rightSection={
                activeJobsCount > 0 ? (
                  <Badge size="xs" variant="filled" color="blue" circle>
                    {activeJobsCount}
                  </Badge>
                ) : undefined
              }
            >
              {t("translation.tabs.batch")}
            </Tabs.Tab>
            <Tabs.Tab
              value="history"
              leftSection={<IconHistory size={16} />}
              rightSection={
                history.entries.length > 0 ? (
                  <Badge size="xs" variant="filled" color="accent" circle>
                    {history.entries.length}
                  </Badge>
                ) : undefined
              }
            >
              {t("translation.tabs.history")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="text" pt="md">
            <TextTranslationView tx={tx} />
          </Tabs.Panel>

          <Tabs.Panel value="batch" pt="md">
            <BatchTranslationView />
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <HistoryPanel
              entries={history.entries}
              onOpen={openFromHistory}
              onRemove={history.remove}
              onClear={history.clear}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

/**
 * Public page component. Wraps the page in BatchJobsProvider so the batch
 * table and the tab badge share one state instance and the auto-poll loop
 * keeps running regardless of which tab is mounted.
 */
export const TranslatePage: React.FC = () => (
  <BatchJobsProvider>
    <TranslatePageInner />
  </BatchJobsProvider>
);
